import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Chip,
  CircularProgress,
  Collapse
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  SyncProblem as SyncProblemIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  VpnKey as TokenIcon
} from '@mui/icons-material';
import MainLayout from './MainLayout';

const SyncStatusPage = () => {
  const { user } = useAuth();
  const [syncData, setSyncData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({
    quickbooks: true,
    clickup: true,
    hubspot: true
  });
  const [refreshing, setRefreshing] = useState({});
  const [resetting, setResetting] = useState({});

  // Group data by service
  const groupedData = {
    quickbooks: syncData.filter(item => item.service === 'quickbooks'),
    clickup: syncData.filter(item => item.service === 'clickup'),
    hubspot: syncData.filter(item => item.service === 'hubspot')
  };

  // Group QuickBooks data by business unit
  const quickbooksBusinessUnits = [...new Set(groupedData.quickbooks.map(item => item.business_unit_id))];

  // Format display name for business unit ID
  const formatBusinessUnitName = (id) => {
    const businessUnitNames = {
      '123145757279014': 'Motion (123145757279014)',
      '193514801304854': 'Ideometry (193514801304854)',
      '9341454074930384': 'New North (9341454074930384)',
      '9341452190397078': 'Spoke (9341452190397078)',
      '14292505': 'ClickUp Workspace',
      '46815402': 'HubSpot Portal'
    };
    return businessUnitNames[id] || `Business Unit ${id}`;
  };

  // Format date with proper EST timezone (UTC-4/UTC-5)
  const formatToEST = (utcDate) => {
    // Create a date object from the UTC string
    const date = new Date(utcDate);
    
    // Convert from UTC to EST (UTC-4 for EDT, UTC-5 for EST)
    // This manually applies the EST/EDT offset
    const isDST = isDaylightSavingsTime(date);
    const offset = isDST ? -4 : -5;
    const estDate = new Date(date.getTime() + (offset * 60 * 60 * 1000));
    
    // Format it using date-fns
    const formattedDate = format(estDate, 'MMM dd, h:mm a');
    
    // Add EST/EDT label based on daylight savings time
    const timeZoneLabel = isDST ? 'EDT' : 'EST';
    return `${formattedDate} ${timeZoneLabel}`;
  };

  // Helper function to determine if a date is during Daylight Saving Time
  const isDaylightSavingsTime = (date) => {
    // Get the standard time offset for January (non-DST)
    const jan = new Date(date.getFullYear(), 0, 1);
    const janOffset = jan.getTimezoneOffset();
    
    // Get the offset for the current date
    const currentOffset = date.getTimezoneOffset();
    
    // If current offset is less than January, it's DST
    return currentOffset < janOffset;
  };

  // Fetch sync status data
  const fetchSyncStatus = async () => {
    try {
      setLoading(true);
      // Use the same URL pattern as elsewhere in the app
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/sync-status`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      const data = await response.json();
      setSyncData(data || []);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Open QuickBooks authorization page in a new tab
  const startQuickBooksAuthorization = (businessUnitId) => {
    try {
      setResetting({ ...resetting, [businessUnitId]: true });
      console.log(`Starting QuickBooks authorization for business unit: ${businessUnitId}`);
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const oauthUrl = `${apiBaseUrl}/api/quickbooks/auth?businessUnitId=${businessUnitId}`;
      console.log(`Opening OAuth URL: ${oauthUrl}`);
      
      // Open the authorization URL in a new tab
      window.open(oauthUrl, '_blank');
      
      // Set a timeout to clear the loading state
      setTimeout(() => {
        setResetting({ ...resetting, [businessUnitId]: false });
      }, 1000);
    } catch (error) {
      console.error('Error starting QuickBooks authorization:', error);
      alert(`Error starting QuickBooks authorization: ${error.message}`);
      setResetting({ ...resetting, [businessUnitId]: false });
    }
  };

  // Trigger a full service sync for a specific service
  const triggerServiceSync = async (service) => {
    const serviceKey = `${service}-full-sync`;
    try {
      setRefreshing(prev => ({ ...prev, [serviceKey]: true }));
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/trigger-service-sync`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ service })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Special case for token authorization requirement
        if (data.requiresAuthorization && service === 'quickbooks') {
          if (window.confirm('QuickBooks authorization required. Would you like to authorize now?')) {
            // Open the authorization URL in a new tab
            window.open(`${apiBaseUrl}${data.authUrl}`, '_blank');
          }
          throw new Error('QuickBooks token refresh required. Please authorize the application.');
        }
        throw new Error(data.error || 'Failed to trigger service sync');
      }
      
      alert(`${service.charAt(0).toUpperCase() + service.slice(1)} sync triggered. ${data.message || ''}`);
      
      // Refresh the data after a short delay
      setTimeout(fetchSyncStatus, 2000);
    } catch (error) {
      console.error(`Error triggering ${service} sync:`, error);
      alert(`Error triggering ${service} sync: ${error.message}`);
    } finally {
      setTimeout(() => {
        setRefreshing(prev => ({ ...prev, [serviceKey]: false }));
      }, 2000);
    }
  };

  useEffect(() => {
    fetchSyncStatus();
    // Refresh data every 5 minutes
    const intervalId = setInterval(fetchSyncStatus, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Toggle group expansion
  const toggleExpand = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Get status chip
  const getStatusChip = (status) => {
    if (status === 'success') {
      return <Chip 
        icon={<CheckIcon />} 
        label="Success" 
        size="small" 
        color="success" 
      />;
    } else if (status === 'partial') {
      return <Chip 
        icon={<WarningIcon />} 
        label="Partial" 
        size="small" 
        color="warning" 
      />;
    } else if (status === 'error') {
      return <Chip 
        icon={<ErrorIcon />} 
        label="Error" 
        size="small" 
        color="error" 
      />;
    } else {
      return <Chip 
        icon={<SyncProblemIcon />} 
        label={status || 'Unknown'} 
        size="small" 
        color="default" 
      />;
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Sync Status
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* QuickBooks Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 0 }}>
                  <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="center" 
                    px={2} 
                    py={1.5} 
                    onClick={() => toggleExpand('quickbooks')}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }}
                  >
                    <Typography variant="h6">
                      QuickBooks Sync Status
                    </Typography>
                    <Box display="flex" alignItems="center">
                      {/* Hide refresh button from team members */}
                      {user?.role !== 'team_member' && (
                        <Tooltip title="Refresh QuickBooks Sync">
                          <IconButton 
                            size="small" 
                            sx={{ color: 'white', mr: 1 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              triggerServiceSync('quickbooks');
                            }}
                            disabled={Boolean(refreshing['quickbooks-full-sync'])}
                          >
                            {refreshing['quickbooks-full-sync'] ? (
                              <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" sx={{ color: 'white' }}>
                        {expandedGroups.quickbooks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Collapse in={expandedGroups.quickbooks}>
                    {quickbooksBusinessUnits.length > 0 ? (
                      quickbooksBusinessUnits.map(businessUnitId => (
                        <Box key={businessUnitId} mb={2} sx={{ px: 2, py: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {formatBusinessUnitName(businessUnitId)}
                            </Typography>
                            {/* Hide reset token button from team members */}
                            {user?.role !== 'team_member' && (
                              <Tooltip title="Open QuickBooks Authorization">
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  size="small"
                                  startIcon={<TokenIcon />}
                                  onClick={() => startQuickBooksAuthorization(businessUnitId)}
                                  disabled={Boolean(resetting[businessUnitId])}
                                >
                                  {resetting[businessUnitId] ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    'Reset Token'
                                  )}
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                          
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Entity Type</TableCell>
                                  <TableCell>Last Sync</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Message</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {groupedData.quickbooks
                                  .filter(item => item.business_unit_id === businessUnitId)
                                  .map(item => {
                                    return (
                                      <TableRow key={item.id}>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight="medium">
                                            {item.entity_type}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Tooltip title={format(new Date(item.last_sync_time), 'yyyy-MM-dd HH:mm:ss')}>
                                            <Typography variant="body2">
                                              {formatToEST(item.last_sync_time)}
                                            </Typography>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                          {getStatusChip(item.last_sync_status)}
                                        </TableCell>
                                        <TableCell>
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              maxWidth: '400px', 
                                              whiteSpace: 'nowrap', 
                                              overflow: 'hidden', 
                                              textOverflow: 'ellipsis' 
                                            }}
                                          >
                                            {item.last_sync_message}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))
                    ) : (
                      <Box p={2} textAlign="center">
                        <Typography variant="body1" color="text.secondary">
                          No QuickBooks sync data available
                        </Typography>
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>

            {/* ClickUp Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 0 }}>
                  <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="center" 
                    px={2} 
                    py={1.5} 
                    onClick={() => toggleExpand('clickup')}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }}
                  >
                    <Typography variant="h6">
                      ClickUp Sync Status
                    </Typography>
                    <Box display="flex" alignItems="center">
                      {/* Hide refresh button from team members */}
                      {user?.role !== 'team_member' && (
                        <Tooltip title="Refresh ClickUp Sync">
                          <IconButton 
                            size="small" 
                            sx={{ color: 'white', mr: 1 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              triggerServiceSync('clickup');
                            }}
                            disabled={Boolean(refreshing['clickup-full-sync'])}
                          >
                            {refreshing['clickup-full-sync'] ? (
                              <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" sx={{ color: 'white' }}>
                        {expandedGroups.clickup ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Collapse in={expandedGroups.clickup}>
                    {groupedData.clickup.length > 0 ? (
                      <Box mb={2} sx={{ px: 2, py: 1 }}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Business Unit</TableCell>
                                <TableCell>Entity Type</TableCell>
                                <TableCell>Last Sync</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Message</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {groupedData.clickup.map(item => {
                                return (
                                  <TableRow key={item.id}>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatBusinessUnitName(item.business_unit_id)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {item.entity_type}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title={format(new Date(item.last_sync_time), 'yyyy-MM-dd HH:mm:ss')}>
                                        <Typography variant="body2">
                                          {formatToEST(item.last_sync_time)}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      {getStatusChip(item.last_sync_status)}
                                    </TableCell>
                                    <TableCell>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          maxWidth: '400px', 
                                          whiteSpace: 'nowrap', 
                                          overflow: 'hidden', 
                                          textOverflow: 'ellipsis' 
                                        }}
                                      >
                                        {item.last_sync_message}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ) : (
                      <Box p={2} textAlign="center">
                        <Typography variant="body1" color="text.secondary">
                          No ClickUp sync data available
                        </Typography>
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>

            {/* HubSpot Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 0 }}>
                  <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="center" 
                    px={2} 
                    py={1.5} 
                    onClick={() => toggleExpand('hubspot')}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }}
                  >
                    <Typography variant="h6">
                      HubSpot Sync Status
                    </Typography>
                    <Box display="flex" alignItems="center">
                      {/* Hide refresh button from team members */}
                      {user?.role !== 'team_member' && (
                        <Tooltip title="Refresh HubSpot Sync">
                          <IconButton 
                            size="small" 
                            sx={{ color: 'white', mr: 1 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              triggerServiceSync('hubspot');
                            }}
                            disabled={Boolean(refreshing['hubspot-full-sync'])}
                          >
                            {refreshing['hubspot-full-sync'] ? (
                              <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" sx={{ color: 'white' }}>
                        {expandedGroups.hubspot ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Collapse in={expandedGroups.hubspot}>
                    {groupedData.hubspot.length > 0 ? (
                      <Box mb={2} sx={{ px: 2, py: 1 }}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Business Unit</TableCell>
                                <TableCell>Entity Type</TableCell>
                                <TableCell>Last Sync</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Message</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {groupedData.hubspot.map(item => {
                                return (
                                  <TableRow key={item.id}>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatBusinessUnitName(item.business_unit_id)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {item.entity_type}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title={format(new Date(item.last_sync_time), 'yyyy-MM-dd HH:mm:ss')}>
                                        <Typography variant="body2">
                                          {formatToEST(item.last_sync_time)}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      {getStatusChip(item.last_sync_status)}
                                    </TableCell>
                                    <TableCell>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          maxWidth: '400px', 
                                          whiteSpace: 'nowrap', 
                                          overflow: 'hidden', 
                                          textOverflow: 'ellipsis' 
                                        }}
                                      >
                                        {item.last_sync_message}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ) : (
                      <Box p={2} textAlign="center">
                        <Typography variant="body1" color="text.secondary">
                          No HubSpot sync data available
                        </Typography>
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </MainLayout>
  );
};

export default SyncStatusPage; 